import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme, THEMES } from '../../context/ThemeContext';
import { Moon, Sun, Check } from 'lucide-react';

/**
 * ThemeSelector - Premium theme toggle for AskAira
 * 
 * Supports:
 * - 🌙 Dark Mode (default, luxury magazine aesthetic)
 * - ☀️ Light Mode (premium, Apple/Notion inspired)
 */

const ThemeSelector = ({ variant = 'full' }) => {
  const { theme, setTheme, toggleTheme, isDark } = useTheme();

  // Compact toggle for sidebar/mobile
  if (variant === 'compact') {
    return (
      <button
        onClick={toggleTheme}
        className="relative flex items-center gap-2 p-2 rounded-xl transition-all duration-300 bg-[var(--bg-card)] hover:bg-[var(--bg-card-hover)] border border-[var(--border-color)]"
        data-testid="theme-toggle-compact"
        title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        <div className="relative w-12 h-6 rounded-full bg-[var(--bg-input)] p-0.5 transition-colors">
          <motion.div
            animate={{ x: isDark ? 0 : 24 }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            className="w-5 h-5 rounded-full flex items-center justify-center shadow-sm"
            style={{ 
              background: isDark 
                ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' 
                : 'linear-gradient(135deg, #f59e0b, #f97316)'
            }}
          >
            {isDark ? (
              <Moon className="w-3 h-3 text-white" />
            ) : (
              <Sun className="w-3 h-3 text-white" />
            )}
          </motion.div>
        </div>
        <span className="text-sm text-[var(--text-secondary)] hidden sm:inline">
          {isDark ? 'Dark' : 'Light'}
        </span>
      </button>
    );
  }

  // Pill toggle variant for nav bars
  if (variant === 'pill') {
    return (
      <div 
        className="flex items-center p-1 rounded-full bg-[var(--bg-card)] border border-[var(--border-color)]"
        data-testid="theme-toggle-pill"
      >
        <button
          onClick={() => setTheme('dark')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            isDark 
              ? 'bg-[var(--color-primary)] text-white shadow-sm' 
              : 'text-[var(--text-tertiary)] hover:text-[var(--text-primary)]'
          }`}
        >
          <Moon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Dark</span>
        </button>
        <button
          onClick={() => setTheme('light')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            !isDark 
              ? 'bg-[var(--color-gold)] text-white shadow-sm' 
              : 'text-[var(--text-tertiary)] hover:text-[var(--text-primary)]'
          }`}
        >
          <Sun className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Light</span>
        </button>
      </div>
    );
  }

  // Icon-only toggle
  if (variant === 'icon') {
    return (
      <button
        onClick={toggleTheme}
        className="p-2 rounded-xl transition-all duration-300 bg-[var(--bg-card)] hover:bg-[var(--bg-card-hover)] border border-[var(--border-color)] hover:border-[var(--border-color-hover)]"
        data-testid="theme-toggle-icon"
        title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={theme}
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 180 }}
            transition={{ duration: 0.2 }}
          >
            {isDark ? (
              <Moon className="w-5 h-5 text-[var(--color-primary)]" />
            ) : (
              <Sun className="w-5 h-5 text-[var(--color-gold)]" />
            )}
          </motion.div>
        </AnimatePresence>
      </button>
    );
  }

  // Full card selector (for settings page)
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">Theme</h3>
          <p className="text-sm text-[var(--text-tertiary)]">Choose your preferred appearance</p>
        </div>
        <button
          onClick={toggleTheme}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[var(--bg-card)] border border-[var(--border-color)] hover:border-[var(--color-primary)] transition-colors"
        >
          {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          <span className="text-sm">{isDark ? 'Dark' : 'Light'}</span>
        </button>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {Object.entries(THEMES).map(([key, config]) => {
          const isActive = theme === key;
          const Icon = key === 'dark' ? Moon : Sun;
          
          return (
            <motion.button
              key={key}
              onClick={() => setTheme(key)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`relative p-5 rounded-2xl border-2 transition-all text-left ${
                isActive 
                  ? 'border-[var(--color-primary)] shadow-lg' 
                  : 'border-[var(--border-color)] hover:border-[var(--border-color-hover)]'
              }`}
              style={{
                backgroundColor: isActive 
                  ? `color-mix(in srgb, ${config.colors.primary} 10%, transparent)`
                  : 'var(--bg-card)'
              }}
              data-testid={`theme-${key}-btn`}
            >
              {isActive && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: config.colors.primary }}
                >
                  <Check className="w-4 h-4 text-white" />
                </motion.div>
              )}
              
              <div className="flex items-center gap-3 mb-3">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ 
                    background: key === 'dark' 
                      ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' 
                      : 'linear-gradient(135deg, #f59e0b, #f97316)'
                  }}
                >
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-[var(--text-primary)]">
                    {config.name}
                  </h4>
                  <p className="text-xs text-[var(--text-tertiary)]">
                    {key === 'dark' ? '🌙 Default' : '☀️ Premium'}
                  </p>
                </div>
              </div>
              
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                {config.description}
              </p>
              
              {/* Color preview */}
              <div className="flex gap-2 mt-4">
                <div 
                  className="w-6 h-6 rounded-full border border-white/20 shadow-sm" 
                  style={{ backgroundColor: config.colors.bgPrimary }}
                  title="Background"
                />
                <div 
                  className="w-6 h-6 rounded-full border border-white/20 shadow-sm" 
                  style={{ backgroundColor: config.colors.primary }}
                  title="Primary"
                />
                <div 
                  className="w-6 h-6 rounded-full border border-white/20 shadow-sm" 
                  style={{ backgroundColor: config.colors.accent }}
                  title="Accent"
                />
                <div 
                  className="w-6 h-6 rounded-full border border-white/20 shadow-sm" 
                  style={{ backgroundColor: config.colors.gold }}
                  title="Gold"
                />
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export { ThemeSelector };
